import {
  edgeGeometry,
  pointRect,
  resolveColor,
  type CanvasModel,
  type CanvasNode,
  type Pt,
  type TempEdge,
} from './model.js';

export interface EdgeLayerProps {
  model: CanvasModel;
  /** Current zoom (edges keep a readable on-screen width at low zoom). */
  scale: number;
  selectedEdgeId: string | null;
  onSelectEdge: (id: string) => void;
  onEditLabel: (id: string) => void;
  tempEdge: TempEdge | null;
}

function arrowPoints(tip: Pt, outDir: Pt, size: number): string {
  // Arrow points into the node: tip at the anchor, base outside along outDir.
  const bx = tip.x + outDir.x * size;
  const by = tip.y + outDir.y * size;
  const px = -outDir.y;
  const py = outDir.x;
  const w = size * 0.55;
  return `${tip.x},${tip.y} ${bx + px * w},${by + py * w} ${bx - px * w},${by - py * w}`;
}

/** Renders inside the world-transform <g> of the underlay SVG. */
export function EdgeLayer(props: EdgeLayerProps) {
  const { model, scale, selectedEdgeId, onSelectEdge, onEditLabel, tempEdge } = props;
  const byId = new Map<string, CanvasNode>(model.nodes.map((n) => [n.id, n]));
  const strokeW = Math.max(2, 2 / scale);
  const hitW = 24 / scale;
  const arrowSize = Math.max(10, 8 / scale);
  const fontSize = Math.max(14, 12 / scale);

  let temp = null;
  if (tempEdge) {
    const a = byId.get(tempEdge.fromNode);
    if (a) {
      const g = edgeGeometry(a, pointRect(tempEdge.to), tempEdge.fromSide, undefined);
      temp = (
        <g>
          <path
            d={g.d}
            fill="none"
            stroke="var(--accent)"
            strokeWidth={strokeW}
            strokeDasharray={`${6 / scale} ${6 / scale}`}
          />
          <polygon points={arrowPoints(g.to, g.toDir, arrowSize)} fill="var(--accent)" />
        </g>
      );
    }
  }

  return (
    <>
      {model.edges.map((e) => {
        const a = byId.get(e.fromNode);
        const b = byId.get(e.toNode);
        if (!a || !b) return null; // endpoints missing (still preserved in the file)
        const g = edgeGeometry(a, b, e.fromSide, e.toSide);
        const sel = e.id === selectedEdgeId;
        const color = sel ? 'var(--accent)' : (resolveColor(e.color) ?? 'var(--text-faint)');
        return (
          <g key={e.id}>
            <path
              d={g.d}
              fill="none"
              stroke={color}
              strokeWidth={sel ? strokeW * 1.6 : strokeW}
            />
            <polygon points={arrowPoints(g.to, g.toDir, arrowSize)} fill={color} />
            <path
              className="cv-edge-hit"
              d={g.d}
              fill="none"
              stroke="transparent"
              strokeWidth={hitW}
              onPointerDown={(ev) => {
                ev.stopPropagation();
                onSelectEdge(e.id);
              }}
              onDoubleClick={() => onEditLabel(e.id)}
            />
            {e.label ? (
              <text
                className="cv-edge-label"
                x={g.mid.x}
                y={g.mid.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={fontSize}
                onPointerDown={(ev) => {
                  ev.stopPropagation();
                  onSelectEdge(e.id);
                }}
                onDoubleClick={() => onEditLabel(e.id)}
              >
                {e.label}
              </text>
            ) : null}
          </g>
        );
      })}
      {temp}
    </>
  );
}
